import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as api from '../../services/api';
import {
  BookOpen, Plus, RefreshCw, Search, ChevronDown, X, Code, Tag,
  Shield, Lock, Database, GitBranch, Settings, Eye, Layers,
  AlertTriangle, CheckCircle, Zap, Copy, ToggleLeft, ToggleRight,
  Trash2, Edit3, FileText, Activity, Filter, Hash, Loader,
  ChevronRight, ExternalLink, Bot,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════
   Credo.ai-Inspired Design Tokens — mirrored from PolicyConfigPage
   ═══════════════════════════════════════════════════════════════════ */
const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)', mid: '#d4453a',
  soft: '#f8d7d4', muted: '#eab3ad', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};
const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};
const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};
const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
};

/* ── Domain Design System ───────────────────────────────────────── */
const DOMAIN_CONFIG = {
  SECURITY:            { bg: '#fef2f2', text: '#991b1b', border: '#fecaca', icon: '#dc2626', Icon: Shield,    label: 'Security' },
  ACCESS_CONTROL:      { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe', icon: '#2563eb', Icon: Lock,      label: 'Access Control' },
  DATA_CLASSIFICATION: { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#7c3aed', Icon: Database,  label: 'Data Classification' },
  CHANGE_MANAGEMENT:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#d97706', Icon: GitBranch, label: 'Change Management' },
  OPERATIONAL:         { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0', icon: '#16a34a', Icon: Settings,  label: 'Operational' },
  COMPLIANCE:          { bg: '#f0f9ff', text: '#075985', border: '#bae6fd', icon: '#0284c7', Icon: Eye,       label: 'Compliance' },
};
const DOMAINS = Object.keys(DOMAIN_CONFIG);

const RULE_TYPES = [
  'REGEX', 'RANGE', 'ENUM', 'APPROVAL_CHAIN', 'RATE_LIMIT',
  'COMPOSITE', 'FIELD_REQUIRED', 'TEMPORAL', 'CUSTOM_SCRIPT', 'DPG_CLASSIFICATION',
];

/* ═══════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════ */
export default function PatternLibraryPanel() {
  const [patterns, setPatterns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [domainFilter, setDomainFilter] = useState('ALL');
  const [ruleTypeFilter, setRuleTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadPatterns = useCallback(async () => {
    setLoading(true);
    try {
      const domain = domainFilter !== 'ALL' ? domainFilter : undefined;
      const [patRes, statsRes] = await Promise.all([
        api.getEnforcementPatterns(domain),
        api.getPatternStats(),
      ]);
      if (patRes.ok) setPatterns(patRes.data || []);
      if (statsRes.ok) setStats(statsRes.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [domainFilter]);

  useEffect(() => { loadPatterns(); }, [loadPatterns]);

  const handleCreate = async (payload) => {
    try {
      const res = await api.createPattern(payload);
      if (res.ok) { setShowCreate(false); await loadPatterns(); }
    } catch (e) { console.error(e); }
  };

  const handleToggle = async (pattern) => {
    try {
      const res = await api.updatePattern(pattern.id, { enabled: !pattern.enabled });
      if (res.ok) await loadPatterns();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (pattern) => {
    try {
      const res = await api.deletePattern(pattern.id);
      if (res.ok) await loadPatterns();
    } catch (e) { console.error(e); }
  };

  /* ── Computed ── */
  const totalPatterns = patterns.length || Object.values(stats).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
  const activePatterns = patterns.filter(p => p.enabled !== false).length;
  const domainsUsed = new Set(patterns.map(p => p.domain)).size;
  const avgConfidence = patterns.length > 0
    ? (patterns.reduce((s, p) => s + (parseFloat(p.confidenceWeight) || 0), 0) / patterns.length).toFixed(2)
    : '—';

  const filtered = useMemo(() => {
    return patterns.filter(p => {
      if (ruleTypeFilter !== 'ALL' && p.mapsToRuleType !== ruleTypeFilter) return false;
      if (statusFilter === 'ENABLED' && p.enabled === false) return false;
      if (statusFilter === 'DISABLED' && p.enabled !== false) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (p.patternName || '').toLowerCase().includes(q)
          || (p.domain || '').toLowerCase().includes(q)
          || (p.mapsToRuleType || '').toLowerCase().includes(q)
          || (p.exampleText || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [patterns, ruleTypeFilter, statusFilter, searchQuery]);

  // Group by domain for the domain distribution bar
  const domainDistribution = useMemo(() => {
    const dist = {};
    DOMAINS.forEach(d => { dist[d] = 0; });
    patterns.forEach(p => { if (dist[p.domain] !== undefined) dist[p.domain]++; });
    return dist;
  }, [patterns]);

  /* ── Render ── */
  return (
    <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>

      {/* ═══ Hero Stats ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Patterns', value: totalPatterns, sub: `Across ${Object.keys(stats).length || domainsUsed} domains`, accent: ACCENT.sky, Icon: Layers },
          { label: 'Active Patterns', value: activePatterns, sub: `${totalPatterns - activePatterns} disabled`, accent: ACCENT.emerald, Icon: CheckCircle },
          { label: 'Domains Covered', value: domainsUsed, sub: `of ${DOMAINS.length} available`, accent: ACCENT.violet, Icon: Tag },
          { label: 'Avg Confidence', value: avgConfidence, sub: 'Weighted extraction score', accent: ACCENT.amber, Icon: Activity },
        ].map((stat, i) => (
          <div key={stat.label} className="pcp-stat-card" style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
            padding: '18px 20px', position: 'relative', overflow: 'hidden',
            animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: stat.accent.icon, opacity: 0.15 }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: stat.accent.text || S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{stat.label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: S[500], marginTop: 6 }}>{stat.sub}</div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: stat.accent.bg, border: `1px solid ${stat.accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <stat.Icon size={18} style={{ color: stat.accent.icon }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ═══ Domain Distribution Bar ═══ */}
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        padding: '16px 20px', marginBottom: 20,
        animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.25s', animationFillMode: 'backwards',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Domain Distribution
          </div>
          <button className="pcp-btn-primary" onClick={() => setShowCreate(true)} style={{
            ...btnBase, padding: '7px 14px', fontSize: 12, fontWeight: 700,
            background: BRAND.primary, color: '#fff', borderRadius: 8,
          }}>
            <Plus size={13} /> Add Custom Pattern
          </button>
        </div>

        {/* Stacked bar */}
        {totalPatterns > 0 && (
          <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
            {DOMAINS.map(d => {
              const count = domainDistribution[d] || 0;
              if (count === 0) return null;
              const pct = (count / totalPatterns) * 100;
              return (
                <div key={d} title={`${DOMAIN_CONFIG[d].label}: ${count}`} style={{
                  width: `${pct}%`, background: DOMAIN_CONFIG[d].icon, transition: 'width 0.3s ease',
                  minWidth: count > 0 ? 4 : 0,
                }} />
              );
            })}
          </div>
        )}

        {/* Domain chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DOMAINS.map(d => {
            const dc = DOMAIN_CONFIG[d];
            const count = domainDistribution[d] || 0;
            const isActive = domainFilter === d;
            const DomainIcon = dc.Icon;
            return (
              <button
                key={d}
                className="pcp-action-btn"
                onClick={() => setDomainFilter(isActive ? 'ALL' : d)}
                style={{
                  ...btnBase, padding: '5px 10px', fontSize: 11, fontWeight: 600,
                  background: isActive ? dc.bg : S[50],
                  color: isActive ? dc.text : S[500],
                  border: `1px solid ${isActive ? dc.border : S[200]}`,
                  borderRadius: 6,
                }}
              >
                <DomainIcon size={12} />
                {dc.label}
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '0 5px', borderRadius: 4,
                  background: isActive ? dc.icon + '18' : S[100],
                  color: isActive ? dc.text : S[400],
                }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ Filter Toolbar ═══ */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap',
        animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.3s', animationFillMode: 'backwards',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: S[400] }} />
          <input
            className="pcp-search"
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search patterns by name, domain, rule type, or example text..."
            style={{ ...inputBase, paddingLeft: 32, paddingRight: searchQuery ? 32 : 12 }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              border: 'none', background: 'none', cursor: 'pointer', color: S[400], padding: 2,
            }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* Rule Type filter */}
        <select
          value={ruleTypeFilter}
          onChange={e => setRuleTypeFilter(e.target.value)}
          style={{ ...inputBase, width: 170 }}
        >
          <option value="ALL">All Rule Types</option>
          {RULE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ ...inputBase, width: 130 }}
        >
          <option value="ALL">All Status</option>
          <option value="ENABLED">Enabled</option>
          <option value="DISABLED">Disabled</option>
        </select>

        {/* Refresh */}
        <button className="pcp-btn-ghost" onClick={loadPatterns} style={{
          ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
          background: S[50], color: S[600], borderRadius: 8,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* ═══ Results Summary ═══ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: S[500] }}>
          Showing <span style={{ fontWeight: 700, color: S[700] }}>{filtered.length}</span> of {totalPatterns} patterns
          {domainFilter !== 'ALL' && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '2px 8px',
              borderRadius: 4, background: DOMAIN_CONFIG[domainFilter]?.bg || S[50],
              color: DOMAIN_CONFIG[domainFilter]?.text || S[600],
              border: `1px solid ${DOMAIN_CONFIG[domainFilter]?.border || S[200]}`,
            }}>
              {DOMAIN_CONFIG[domainFilter]?.label}
              <button onClick={() => setDomainFilter('ALL')} style={{
                border: 'none', background: 'none', cursor: 'pointer', marginLeft: 4,
                color: DOMAIN_CONFIG[domainFilter]?.text || S[500], fontSize: 10, padding: 0,
              }}><X size={10} /></button>
            </span>
          )}
        </div>
      </div>

      {/* ═══ Loading ═══ */}
      {loading && (
        <div style={{ padding: '48px 0', textAlign: 'center' }}>
          <Loader size={24} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary, margin: '0 auto' }} />
          <div style={{ fontSize: 13, color: S[500], marginTop: 12 }}>Loading enforcement patterns...</div>
        </div>
      )}

      {/* ═══ Pattern Cards ═══ */}
      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((p, i) => (
            <PatternCard
              key={p.id}
              pattern={p}
              index={i}
              expanded={expandedId === p.id}
              onToggleExpand={() => setExpandedId(expandedId === p.id ? null : p.id)}
              onToggleEnable={() => handleToggle(p)}
              onDelete={() => handleDelete(p)}
            />
          ))}
        </div>
      )}

      {/* ═══ Empty State ═══ */}
      {!loading && filtered.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '60px 24px',
          animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.2s', animationFillMode: 'backwards',
        }}>
          <BookOpen size={36} style={{ color: S[300], marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: S[700], marginBottom: 4 }}>
            {totalPatterns === 0 ? 'No Enforcement Patterns' : 'No patterns match the current filters'}
          </div>
          <div style={{ fontSize: 13, color: S[500], maxWidth: 420, margin: '0 auto' }}>
            {totalPatterns === 0
              ? 'Create enforcement patterns to define reusable extraction templates that map policy text to enforcement rules.'
              : 'Try adjusting your search query or filters to find what you\'re looking for.'
            }
          </div>
          {totalPatterns === 0 && (
            <button className="pcp-btn-primary" onClick={() => setShowCreate(true)} style={{
              ...btnBase, padding: '10px 20px', fontSize: 13, fontWeight: 700,
              background: BRAND.primary, color: '#fff', borderRadius: 8, marginTop: 16,
            }}>
              <Plus size={14} /> Create First Pattern
            </button>
          )}
        </div>
      )}

      {/* ═══ Create Modal ═══ */}
      {showCreate && (
        <CreatePatternModal onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Pattern Card — Expandable, domain-coded
   ═══════════════════════════════════════════════════════════════════ */
function PatternCard({ pattern: p, index, expanded, onToggleExpand, onToggleEnable, onDelete }) {
  const dc = DOMAIN_CONFIG[p.domain] || DOMAIN_CONFIG.COMPLIANCE;
  const DomainIcon = dc.Icon || Tag;
  const isDisabled = p.enabled === false;

  return (
    <div className="pcp-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
      borderLeft: `4px solid ${isDisabled ? S[300] : dc.icon}`,
      opacity: isDisabled ? 0.7 : 1,
      animation: 'pcpSlideUp 0.3s ease', animationDelay: `${0.1 + index * 0.03}s`, animationFillMode: 'backwards',
    }}>
      {/* ── Header (clickable) ── */}
      <div style={{ padding: '16px 18px', cursor: 'pointer' }} onClick={onToggleExpand}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, flex: 1, minWidth: 0 }}>
            {/* Domain icon badge */}
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: dc.bg,
              border: `1px solid ${dc.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <DomainIcon size={18} style={{ color: dc.icon }} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Title + badges */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: S[900] }}>
                  {p.patternName}
                </span>

                {/* Domain badge */}
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: dc.bg, color: dc.text, border: `1px solid ${dc.border}`,
                }}>
                  {dc.label}
                </span>

                {/* Rule type badge */}
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: ACCENT.slate.bg, color: ACCENT.slate.text, border: `1px solid ${ACCENT.slate.border}`,
                }}>
                  {(p.mapsToRuleType || '').replace(/_/g, ' ')}
                </span>

                {/* Custom badge */}
                {p.tenantId && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    background: ACCENT.amber.bg, color: ACCENT.amber.text, border: `1px solid ${ACCENT.amber.border}`,
                  }}>
                    Custom
                  </span>
                )}

                {/* Disabled badge */}
                {isDisabled && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    background: ACCENT.rose.bg, color: ACCENT.rose.text, border: `1px solid ${ACCENT.rose.border}`,
                  }}>
                    Disabled
                  </span>
                )}
              </div>

              {/* Metadata row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: S[500] }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Activity size={11} />
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>
                    {p.confidenceWeight}
                  </span>
                  <span>confidence</span>
                </span>
                {p.exampleText && (
                  <span style={{
                    maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontStyle: 'italic', color: S[400],
                  }}>
                    "{p.exampleText}"
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Expand chevron */}
          <ChevronDown size={16} style={{
            color: S[400], flexShrink: 0, marginTop: 4,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s ease',
          }} />
        </div>
      </div>

      {/* ── Expanded Detail ── */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${S[100]}`, padding: '16px 18px' }}>
          {/* Technical details grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {/* Pattern Regex */}
            <div style={{ gridColumn: p.extractionTemplate ? '1' : '1 / -1' }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase',
                letterSpacing: '0.08em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Code size={11} /> Pattern Regex
              </div>
              <div style={{
                padding: '10px 14px', borderRadius: 8, background: S[900],
                fontSize: 12, color: ACCENT.emerald.icon, fontFamily: "'IBM Plex Mono', monospace",
                lineHeight: 1.7, wordBreak: 'break-all', position: 'relative',
              }}>
                <span style={{ opacity: 0.5, color: S[400] }}>/</span>
                {p.patternRegex}
                <span style={{ opacity: 0.5, color: S[400] }}>/</span>
              </div>
            </div>

            {/* Extraction Template */}
            {p.extractionTemplate && (
              <div>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <FileText size={11} /> Extraction Template
                </div>
                <div style={{
                  padding: '10px 14px', borderRadius: 8, background: S[900],
                  fontSize: 12, color: ACCENT.sky.icon, fontFamily: "'IBM Plex Mono', monospace",
                  lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 160, overflowY: 'auto',
                }}>
                  {typeof p.extractionTemplate === 'string'
                    ? p.extractionTemplate
                    : JSON.stringify(p.extractionTemplate, null, 2)}
                </div>
              </div>
            )}
          </div>

          {/* Example Text */}
          {p.exampleText && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase',
                letterSpacing: '0.08em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <BookOpen size={11} /> Example Match Text
              </div>
              <div style={{
                padding: '10px 14px', borderRadius: 8, background: dc.bg,
                border: `1px solid ${dc.border}`,
                fontSize: 12, color: dc.text, fontStyle: 'italic', lineHeight: 1.6,
              }}>
                "{p.exampleText}"
              </div>
            </div>
          )}

          {/* Metadata pills */}
          <div style={{
            display: 'flex', gap: 12, padding: '10px 14px', borderRadius: 8,
            background: S[50], border: `1px solid ${S[100]}`, marginBottom: 16,
            flexWrap: 'wrap',
          }}>
            {[
              { label: 'ID', value: (p.id || '').substring(0, 12) + '...' },
              { label: 'Domain', value: dc.label },
              { label: 'Rule Type', value: (p.mapsToRuleType || '').replace(/_/g, ' ') },
              { label: 'Confidence', value: p.confidenceWeight },
              { label: 'Status', value: isDisabled ? 'Disabled' : 'Enabled' },
              ...(p.tenantId ? [{ label: 'Scope', value: 'Tenant-specific' }] : [{ label: 'Scope', value: 'Global' }]),
            ].map(m => (
              <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}:</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: S[700], fontFamily: "'IBM Plex Mono', monospace" }}>{m.value}</span>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="pcp-action-btn" onClick={(e) => { e.stopPropagation(); onToggleEnable(); }} style={{
              ...btnBase, padding: '6px 12px', fontSize: 11, fontWeight: 600,
              background: isDisabled ? ACCENT.emerald.bg : ACCENT.amber.bg,
              color: isDisabled ? ACCENT.emerald.text : ACCENT.amber.text,
              border: `1px solid ${isDisabled ? ACCENT.emerald.border : ACCENT.amber.border}`,
              borderRadius: 6,
            }}>
              {isDisabled ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
              {isDisabled ? 'Enable' : 'Disable'}
            </button>

            <button className="pcp-action-btn" onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{
              ...btnBase, padding: '6px 12px', fontSize: 11, fontWeight: 600,
              background: ACCENT.rose.bg, color: ACCENT.rose.text,
              border: `1px solid ${ACCENT.rose.border}`, borderRadius: 6,
            }}>
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Create Pattern Modal — Credo.ai Styled
   ═══════════════════════════════════════════════════════════════════ */
function CreatePatternModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    domain: 'SECURITY', patternName: '', patternRegex: '',
    mapsToRuleType: 'REGEX', exampleText: '', extractionTemplate: '{}',
    confidenceWeight: '1.0',
  });
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('basic');

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = () => {
    if (!form.patternName.trim()) { setError('Pattern name is required'); return; }
    if (!form.patternRegex.trim()) { setError('Pattern regex is required'); return; }
    try {
      new RegExp(form.patternRegex);
    } catch {
      setError('Invalid regex syntax'); return;
    }
    try {
      const template = JSON.parse(form.extractionTemplate);
      onSave({
        ...form,
        extractionTemplate: template,
        confidenceWeight: parseFloat(form.confidenceWeight) || 1.0,
      });
    } catch {
      setError('Invalid JSON in extraction template');
    }
  };

  const dc = DOMAIN_CONFIG[form.domain] || DOMAIN_CONFIG.SECURITY;

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: S[600],
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28, 25, 23, 0.5)',
      backdropFilter: 'blur(4px)', zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'pcpFadeIn 0.2s ease',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, width: 600,
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        animation: 'pcpSlideUp 0.3s ease',
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${S[200]}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: BRAND.gradientSubtle,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: dc.bg,
              border: `1px solid ${dc.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <dc.Icon size={18} style={{ color: dc.icon }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: S[900] }}>Create Enforcement Pattern</div>
              <div style={{ fontSize: 12, color: S[500] }}>Define a reusable extraction template for policy text</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: S[100], cursor: 'pointer',
            width: 32, height: 32, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: S[500],
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin: '16px 24px 0', padding: '10px 14px', borderRadius: 8,
            background: ACCENT.rose.bg, color: ACCENT.rose.text,
            border: `1px solid ${ACCENT.rose.border}`,
            fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertTriangle size={14} />
            {error}
            <button onClick={() => setError(null)} style={{
              border: 'none', background: 'none', cursor: 'pointer',
              color: ACCENT.rose.text, marginLeft: 'auto', padding: 0,
            }}><X size={12} /></button>
          </div>
        )}

        {/* Form Content */}
        <div style={{ padding: '20px 24px' }}>

          {/* ── Section: Classification ── */}
          <div style={{
            fontSize: 10, fontWeight: 700, color: BRAND.primary, textTransform: 'uppercase',
            letterSpacing: '0.1em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Tag size={11} /> Classification
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Domain</label>
              <select value={form.domain} onChange={e => update('domain', e.target.value)} style={inputBase}>
                {DOMAINS.map(d => (
                  <option key={d} value={d}>{DOMAIN_CONFIG[d].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Maps to Rule Type</label>
              <select value={form.mapsToRuleType} onChange={e => update('mapsToRuleType', e.target.value)} style={inputBase}>
                {RULE_TYPES.map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Section: Identity ── */}
          <div style={{
            fontSize: 10, fontWeight: 700, color: BRAND.primary, textTransform: 'uppercase',
            letterSpacing: '0.1em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Hash size={11} /> Identity
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Pattern Name *</label>
              <input
                value={form.patternName}
                onChange={e => update('patternName', e.target.value)}
                placeholder="e.g., Password Minimum Length, Data Retention Period"
                style={inputBase}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}>
              <div>
                <label style={labelStyle}>Example Text</label>
                <input
                  value={form.exampleText}
                  onChange={e => update('exampleText', e.target.value)}
                  placeholder="Example policy text that this pattern matches"
                  style={inputBase}
                />
              </div>
              <div>
                <label style={labelStyle}>Confidence</label>
                <input
                  value={form.confidenceWeight}
                  onChange={e => update('confidenceWeight', e.target.value)}
                  type="number" step="0.1" min="0" max="1"
                  style={{ ...inputBase, width: 100, textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}
                />
              </div>
            </div>
          </div>

          {/* ── Section: Technical ── */}
          <div style={{
            fontSize: 10, fontWeight: 700, color: BRAND.primary, textTransform: 'uppercase',
            letterSpacing: '0.1em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Code size={11} /> Technical Definition
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Pattern Regex *</label>
            <textarea
              value={form.patternRegex}
              onChange={e => update('patternRegex', e.target.value)}
              placeholder="(?i)password\s+(?:must\s+be|minimum|at\s+least)\s+(\d+)\s+characters?"
              rows={3}
              style={{
                ...inputBase, minHeight: 72, fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12, resize: 'vertical', lineHeight: 1.6,
                background: S[900], color: ACCENT.emerald.icon,
                border: `1px solid ${S[700]}`,
              }}
            />
            {/* Regex validity indicator */}
            {form.patternRegex && (() => {
              try { new RegExp(form.patternRegex); return (
                <div style={{ fontSize: 10, color: ACCENT.emerald.text, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle size={10} /> Valid regex
                </div>
              ); } catch { return (
                <div style={{ fontSize: 10, color: ACCENT.rose.text, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={10} /> Invalid regex syntax
                </div>
              ); }
            })()}
          </div>

          <div>
            <label style={labelStyle}>Extraction Template (JSON)</label>
            <textarea
              value={form.extractionTemplate}
              onChange={e => update('extractionTemplate', e.target.value)}
              placeholder='{"threshold": "$1", "unit": "characters", "enforcement": "BLOCK"}'
              rows={4}
              style={{
                ...inputBase, minHeight: 90, fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12, resize: 'vertical', lineHeight: 1.6,
                background: S[900], color: ACCENT.sky.icon,
                border: `1px solid ${S[700]}`,
              }}
            />
            <div style={{ fontSize: 10, color: S[400], marginTop: 4 }}>
              Use $1, $2 placeholders to reference regex capture groups
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${S[200]}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          background: S[25],
        }}>
          <button className="pcp-btn-ghost" onClick={onClose} style={{
            ...btnBase, padding: '9px 18px', fontSize: 13, fontWeight: 600,
            background: '#fff', color: S[600], borderRadius: 8,
            border: `1px solid ${S[200]}`,
          }}>
            Cancel
          </button>
          <button className="pcp-btn-primary" onClick={handleSave} style={{
            ...btnBase, padding: '9px 18px', fontSize: 13, fontWeight: 700,
            background: BRAND.primary, color: '#fff', borderRadius: 8,
          }}>
            <Plus size={14} /> Create Pattern
          </button>
        </div>
      </div>
    </div>
  );
}
